import { FC, useEffect, useState } from 'react';
import { LockOptions, LockRequest } from 'types';
import { OfferingDetails } from 'components/business/offering/OfferingDetails';
import { SearchTimeMap, Stats } from 'utils/calendar';
import { WaitlistRequestMetadata } from 'routes/business/search/ExperienceSearchRoute';
import { consumer, core } from '@tock/coretypes';
import { isOfferedOnOneDateAndAtOneTime, isOfferingEventType } from 'utils/offerings';
import BallPulse from '@tock/shared/components/BallPulse';
import SearchResultMini from 'components/business/search/SearchResultMini';
import _ from 'lodash';

export interface ReservationSearchResultsProps {
  availableDates: DateMap;
  availableTimes: Dictionary<Stats>;
  business: consumer.IBusinessConfig;
  callToActionHint?: string;
  exclusivityAvailable?: core.enums.Exclusivity[];
  offerings: consumer.ConsumerOfferings;
  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  navigatingExperienceId: number | null;
  navigationTransition: boolean;
  onNavigateToExperience(event: consumer.Offering, nextAvailableDate?: string): void;
  onSelect(request: LockRequest, options: LockOptions): void;
  onShowWaitlist(experienceId: number, requestMetadata?: WaitlistRequestMetadata): void;
  selectedDate: string;
  selectedPartySize: number;
  selectedPurchaseSize: number;
  selectedTime: string;
  showWaitlist: boolean;
  ticketType: core.TicketType;
  times: SearchTimeMap;
}

export const ReservationSearchResults: FC<ReservationSearchResultsProps> = ({
  availableDates,
  availableTimes,
  business,
  callToActionHint,
  exclusivityAvailable,
  offerings,
  lockedDateTime,
  lockedSeatingArea,
  navigatingExperienceId,
  navigationTransition,
  onNavigateToExperience,
  onSelect,
  onShowWaitlist,
  selectedDate,
  selectedPartySize,
  selectedPurchaseSize,
  selectedTime,
  showWaitlist,
  ticketType,
  times,
}) => {
  const showTransition = navigationTransition && navigatingExperienceId === ticketType.id;
  const experience = _.find(offerings.experience, { id: ticketType.id }) as consumer.Offering;

  const [selectedSeatingAreaId, setSelectedSeatingAreaId] = useState<undefined | number>();

  useEffect(() => {
    // Update selectedSeatingAreaId when selectedDate changes to differ from am possible lockedDateTime
    if (
      !!lockedSeatingArea?.id &&
      experience?.id === ticketType.id &&
      !!lockedDateTime &&
      lockedDateTime.startsWith(selectedDate)
    ) {
      setSelectedSeatingAreaId(lockedSeatingArea.id);
    } else {
      setSelectedSeatingAreaId(undefined);
    }
  }, [experience?.id, lockedDateTime, lockedSeatingArea, selectedDate, ticketType.id]);

  if (!experience) {
    return null;
  }

  const handleSelectTime = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dateTime: any,
    priceCents: number,
    options: LockOptions = { showError: true }
  ) => {
    const request = {
      dateTime,
      partySize: selectedPurchaseSize,
      priceCents,
      ticketTypeId: ticketType.id,
      selectedSeatingAreaId: options.seatingAreaId || selectedSeatingAreaId || undefined,
    };

    onSelect(request, options);
  };

  return (
    <div
      className="SearchModalExperiences-item Consumer-reservation"
      data-testid="search-result-experience-item"
    >
      {showTransition && (
        <div className="SearchModal-experienceNavigationOverlay">
          <BallPulse data-testid="ReservationSearchResultsBallPulse" />
        </div>
      )}

      <OfferingDetails
        business={business}
        offering={experience}
        onNavigateToExperience={onNavigateToExperience}
        showExperienceImages={false}
      />

      <div className="SearchModalExperiences-itemTimes">
        <SearchResultMini
          availableDates={availableDates}
          availableTimes={availableTimes}
          businessEmail={business.email}
          businessId={business?.id ?? undefined}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          businessPhone={business.customerFacingPhone!}
          callToActionHint={callToActionHint}
          exclusivityAvailable={exclusivityAvailable || []}
          isEventVariety={isOfferingEventType(experience)}
          isOfferedAtOneDateTime={isOfferedOnOneDateAndAtOneTime(experience)}
          lockedDateTime={lockedDateTime}
          lockedSeatingArea={lockedSeatingArea}
          seatingAreas={experience.seatingArea}
          selectedDate={selectedDate}
          selectedPartySize={selectedPartySize}
          selectedPurchaseSize={selectedPurchaseSize}
          selectedTime={selectedTime}
          selectedSeatingAreaId={selectedSeatingAreaId}
          showWaitlist={showWaitlist}
          ticketType={ticketType}
          times={times}
          onSelect={handleSelectTime}
          onShowWaitlist={onShowWaitlist}
          onNavigateToExperience={onNavigateToExperience}
          offering={experience}
        />
      </div>
    </div>
  );
};
